/* Copyright (c) 2022 MOCA Systems, Inc. All rights reserved. */

import { Injectable, OnDestroy } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { defer, Observable, Subject } from 'rxjs';
import { map, startWith, filter, distinctUntilChanged } from 'rxjs/operators';
import { IChangedValues } from 'src/app/common/form-utils/ChangedValues';
import { valueChangesOfChildren } from 'src/app/plain-javascript/services/rxjs-form-extensions';

type IFormTypeConfig<T extends string | number, R> = {
	[Property in T]: readonly R[];
};

/**
 * This is intended to be a component provided service
 * The lifecycle intent is something like…
 * - the form manager and the form are 1 to 1, so every time a new form is needs, we’ll also need a new form manager.
 *   - technically this is an angular service, and not super conducive to having multiple instances, but the component provider array will
 *     allow for one per component. And absolutely worst case, there's really not much stopping you from manually instantiating an instance.
 * - most likely this means that the normal view has one set and the edit dialog has a second set (each component will need to provide a formManager)
 * - when doing single edit (the only thing supported for now) the form will be recreated each time it’s opened and then closed
 * - when doing a multi edit and switching between types, the form will not be recreated and the fields will just need to be more dynamically swapped around
 */

//Model === GroupedTicket/ IReportOptions
//FormType === TicketTypeCombined/ ReportType
//SupportedFormControls === TicketFormControls/ ReportFormControl
//FormTypeConfig === ticketFormTypeConfig/ reportsFormTypeConfig

@Injectable() //angular was absolutely adamant that this "class that can't be initialized", _needs_ a decorator because it has a ngOnDestroy method
export abstract class AbstractFormManager<
	Model,
	FormType extends string | number,
	SupportedFormControls extends string,
	FormTypeConfig extends IFormTypeConfig<FormType, SupportedFormControls> = IFormTypeConfig<
		FormType,
		SupportedFormControls
	>
> implements OnDestroy
{
	/** archive of the last emitted model instance, can be undefined if there's no emission */
	protected lastModelUpdate: Model;

	private internalFormType: FormType;
	/** The current set of formControls that _should_ be enabled, including potential dynamic controls */
	private cachedControlsByType = new Set<SupportedFormControls>();

	protected formGroup: UntypedFormGroup;
	/** the set of pre-created formControls that are not active */
	private inactiveFormControls: Map<SupportedFormControls, AbstractControl>;

	protected abstract formTypeConfig: FormTypeConfig;
	/** spec dynamic form inclusion, that overrides the configs. Such as with feature flags */
	protected dynamicConfigOverrides: Map<SupportedFormControls, boolean> = new Map();

	/** listen for user changes, this will be undefined before create is called */
	public valueChanges: Observable<IChangedValues>;
	//FUTURE POTENTIAL ADDITION - a variant of valueChanges that:
	// - sync emits once for all current values on the form
	// - and then subscribes to normal valueChanges
	// Would serve the purpose of "a subscriber joining late" and being brought up to speed

	/**
	 * a subject that emits whenever a control is added or removed from the from. Is handled the old-fashioned
	 * way by calling it in the methods that manipulate it
	 */
	protected controlsAddedOrRemoved = new Subject<void>();

	/**
	 * Observable that emits whenever controls are added to and removed from the group with the current set of enabled controls
	 * Will always emit once synchronously on subscription
	 * - "in the form" is represented as true
	 * - "not in the form" is not represented at all, and currently removed controls will not be in the object
	 */
	public activeControl$: Observable<{ [key in SupportedFormControls]: true }>;

	/** a mapping from the form control name to the data in the model object */
	protected abstract getControlDataFromModel<T>(controlName: SupportedFormControls, model: Model): T;

	/** define the formGroup, anything done here applies to the form before any listeners are set up */
	protected abstract createFormGroup(): UntypedFormGroup; //could pass in initial data, but this enforces the "start null, call patch" pattern

	/** cached version of the last selected formType */
	protected set currentFormType(type: FormType) {
		this.internalFormType = type;
		this.cachedControlsByType = new Set(this.formTypeConfig[type]);
	}
	protected get currentFormType() {
		return this.internalFormType;
	}

	constructor(protected formBuilder: UntypedFormBuilder) {}

	/**
	 * Apply the model data to the form. Intended to be overridden
	 * - consider a type argument as well, better support for models that don't self-contain the type
	 */
	public patchForm(model: Model) {
		this.lastModelUpdate = model;
	}

	/**
	 * Merge partial updates into lastModelUpdate
	 * @param partialModel
	 */
	public patchFormPartial(partialModel: Partial<Model>): void {
		this.lastModelUpdate = {
			...this.lastModelUpdate,
			...partialModel,
		};
	}

	/**
	 * Initializes this service. Intended to be overridden
	 */
	public create(formType: FormType, model: Model, isDisabled?: boolean) {
		if (!formType) {
			throw new Error('A valid FormType must be provided');
		}
		if (!model) {
			throw new Error('A valid Model must be provided');
		}

		this.currentFormType = formType;
		this.inactiveFormControls = new Map();

		this.formGroup = this.createFormGroup();

		this.patchForm(model);

		this.valueChanges = valueChangesOfChildren(this.formGroup, this.controlsAddedOrRemoved);
		this.resetFormTo(this.formTypeConfig[formType]);

		this.activeControl$ = defer(() =>
			this.controlsAddedOrRemoved.pipe(
				startWith(this.formGroup.controls),
				map(() => this.formGroup.controls),
				map((controls) => {
					const safeControls = controls || {};
					const newObj: { [key in SupportedFormControls]: true } = {} as {
						[key in SupportedFormControls]: true;
					};
					Object.keys(safeControls).forEach((key) => (newObj[key] = true));
					return newObj;
				})
			)
		);

		if (isDisabled) {
			//truthy check
			Object.keys(this.formGroup.controls).forEach((key) => {
				this.formGroup.controls[key].disable();
			});
		}

		return this.formGroup;
	}

	protected shouldDynamicallyManipulateSpecificControls(controlNames: SupportedFormControls[]) {
		return controlNames.every((name) => this.cachedControlsByType.has(name));
	}

	//maybe make public?
	protected resetFormTo(
		controlNamesToKeep: readonly SupportedFormControls[],
		skipFormChangeNotification = false
	) {
		//index to avoid array searches, since it's iterating the list once _anyways_
		const index = new Map<string, boolean>();

		//apply overrides
		let overridenControlNamesToKeep = controlNamesToKeep;
		if (this.dynamicConfigOverrides.size) {
			//clone it
			const innerControlNamesToKeep = [...controlNamesToKeep];
			this.dynamicConfigOverrides.forEach((val, key) => {
				const index = innerControlNamesToKeep.indexOf(key);
				const exists = index !== -1;
				if (exists && !val) {
					innerControlNamesToKeep.splice(index, 1);
				}
				if (!exists && val) {
					innerControlNamesToKeep.push(key);
				}
			});
			overridenControlNamesToKeep = innerControlNamesToKeep;
		}

		//if in the passed in list, add it
		overridenControlNamesToKeep.forEach((name) => {
			this.addFormControl(name, skipFormChangeNotification);
			index.set(name, true);
		});

		//if in the current controls and not in the passed in list, remove it
		for (const name of Object.keys(this.formGroup.controls)) {
			if (!index.has(name)) {
				this.removeFormControl(name as SupportedFormControls, skipFormChangeNotification);
			}
		}
	}

	/** change the type of the form. Resetting the form to a clean state and causing a full set of value changes emissions */
	protected changeFormType(formType: FormType) {
		this.currentFormType = formType;
		this.resetFormTo(this.formTypeConfig[formType], true);
		this.controlsAddedOrRemoved.next();
		this.makePristine();
	}

	//unsure if this is actually useful?
	protected emptyForm() {
		if (this.formGroup?.controls && Object.keys(this.formGroup.controls).length === 0) {
			return; //nothing to do if it's empty
		}

		for (const name of Object.keys(this.formGroup.controls)) {
			this.removeFormControl(name as SupportedFormControls, true);
		}

		this.controlsAddedOrRemoved.next();
		this.makePristine();
	}

	/** reset the form to a clean/ uninteracted state */
	protected makePristine() {
		this.formGroup.markAsPristine();
		this.formGroup.markAsUntouched();

		this.inactiveFormControls.forEach((control, controlName) => {
			control.markAsPristine();
			control.markAsUntouched();
		});
	}

	/**
	 * Adds a form control to the formGroup by name. Can potentially throw errors if the name is wrong
	 * @param skipFormChangeNotification By default, this will trigger an emission on [[controlsAddedOrRemoved]]
	 * By passing true, this will be suppressed. Only do this if you're going to call it manually after a batch operation
	 * Controls be default to null if the model is not defined
	 */
	protected addFormControl(
		controlName: SupportedFormControls,
		skipFormChangeNotification = false,
		skipControlValueReset = false
	) {
		//no-op, form already contains the control
		if (this.formGroup.get(controlName)) {
			return;
		}

		if (!this.inactiveFormControls.has(controlName)) {
			throw new Error(`tried to add a control that does not exist. "${controlName}"`);
		}

		const controlToBeAdded = this.inactiveFormControls.get(controlName);

		//update if more complex form update logic is necessary
		if (!skipControlValueReset) {
			const modelData = this.lastModelUpdate
				? this.getControlDataFromModel(controlName, this.lastModelUpdate)
				: null;
			controlToBeAdded.setValue(modelData);
		}

		this.formGroup.addControl(controlName, controlToBeAdded);
		if (!skipFormChangeNotification) {
			this.controlsAddedOrRemoved.next();
		}
	}

	/**
	 * Removes a form control from the formGroup by name. Can potentially throw errors if the name is wrong
	 * @param skipFormChangeNotification By default, this will trigger an emission on [[controlsAddedOrRemoved]]
	 * By passing true, this will be suppressed. Only do this if you're going to call it manually after a batch operation
	 */
	protected removeFormControl(controlName: SupportedFormControls, skipFormChangeNotification = false) {
		//no-op, control already in inactive
		if (!this.formGroup.get(controlName) && this.inactiveFormControls.has(controlName)) {
			return;
		}

		//ok-ish error check, don't think we need to be too comprehensive in the remove case
		if (!this.formGroup.get(controlName) && !this.inactiveFormControls.has(controlName)) {
			throw new Error(`tried to remove a control that does not exist. "${controlName}"`);
		}

		this.inactiveFormControls.set(controlName, this.formGroup.get(controlName));
		this.formGroup.removeControl(controlName);

		if (!skipFormChangeNotification) {
			this.controlsAddedOrRemoved.next();
		}
	}

	/**
	 * Convenience method that returns an observable which emits only when a specific control changes.
	 * Caller has to unsubscribe.
	 */
	public valueChangesFor(controlName: SupportedFormControls) {
		return this.valueChanges.pipe(
			filter((fullObj) => controlName in fullObj),
			map((fullObj) => fullObj[controlName]),
			distinctUntilChanged()
		);
	}

	/**
	 * Override the active state of a control, useful for something like a feature flag
	 * TODO - consider improving api and testing
	 */
	public setOverrideTypeConfig(typeConfig: typeof this.dynamicConfigOverrides, runReset = true) {
		this.dynamicConfigOverrides = typeConfig;
		if (runReset) {
			this.resetFormTo(this.formTypeConfig[this.currentFormType], true);
			this.controlsAddedOrRemoved.next();
		}
	}

	/**
	 * This will be called automatically if provided as a component service
	 */
	public ngOnDestroy() {
		this.controlsAddedOrRemoved.complete();
	}
}
